import { ReactNode } from 'react';
import AvatarChip from '../../../../shared/avatar-chip';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface LabelDetailsPanelProps {
  labelId: string;
  creator: string;
  lastModified: number;
  /** The label's status; Create Label always shows a draft. */
  status?: string;
  /** Renders just the section, without its own card, inside another panel. */
  embedded?: boolean;
}

/** Shown for a detail that has no value yet, like the design. */
const EMPTY_VALUE = '--';

export default function LabelDetailsPanel({
  labelId,
  creator,
  lastModified,
  status = 'Draft',
  embedded = false,
}: LabelDetailsPanelProps) {
  const details: [string, ReactNode][] = [
    ['Status', status],
    ['Label ID', labelId || EMPTY_VALUE],
    [
      'Creator',
      creator ? (
        <AvatarChip email={creator} type="User" label={creator} size="small" />
      ) : (
        EMPTY_VALUE
      ),
    ],
    [
      'Last modified',
      lastModified ? new Date(lastModified).toLocaleString() : EMPTY_VALUE,
    ],
  ];

  const content = (
    <Stack spacing={1} sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 500 }}>Label details</Typography>
      {details.map(([key, value]) => (
        <Stack direction="row" spacing={2} key={key} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ width: 100, flexShrink: 0 }}
          >
            {key}
          </Typography>
          {typeof value === 'string' ? (
            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
              {value}
            </Typography>
          ) : (
            <Box sx={{ minWidth: 0 }}>{value}</Box>
          )}
        </Stack>
      ))}
    </Stack>
  );

  return embedded ? content : <Card sx={{ borderRadius: 2 }}>{content}</Card>;
}
